/**
 * Whether there is a network, and being told when that changes.
 *
 * The scorebook does not block recording on this. It queues and syncs
 * regardless, per BUILD_PLAN 5.8, and mostly this exists to TELL somebody what
 * is happening: a sync indicator that says "saved on this device" is the point.
 *
 * One decision does rest on it. A Finish or Reopen is not sent while this says
 * offline (`refuseIfOffline` in `apps/web/src/scorebook/useScorebook.ts`),
 * because only a request that never left can be reported as "did not go
 * through"; one that was sent and got no answer may have committed. The
 * consequence of a false "offline": that Finish or Reopen is refused until the
 * hint flips, nothing is lost (the outbox keeps every play and the coach can
 * tap again), and the copy ("No signal, so that did not go through") is true,
 * because nothing was sent.
 *
 * `navigator.onLine` is famously weak: it reports whether an interface is up,
 * not whether anything is reachable, and a gym wifi captive portal answers true
 * while dropping every request. So this is a hint: "true" is never taken as
 * proof of reach, and nothing may treat it as permission to skip a retry.
 */

import { browserNavigator, browserWindow, type Adapter } from './host.js';

export interface Network extends Adapter {
  /** A HINT. False is reliable; true only means an interface exists. */
  readonly isOnline: () => boolean;
  /** Returns an unsubscribe function. Never throws when unsupported. */
  readonly onChange: (listener: (online: boolean) => void) => () => void;
}

/**
 * The CAPABILITY, not the object.
 *
 * Node 21 and later expose a global `navigator` with `userAgent` and
 * `hardwareConcurrency` and no `onLine` at all, so checking that a navigator
 * exists reported support in a host that has none, and `isOnline()` returned
 * `undefined` where the type said boolean. Caught by the adapter contract test,
 * which is the whole reason it runs in the `node` project rather than `dom`.
 *
 * Every capability check in this package should ask about the capability for
 * the same reason.
 */
function onLineCapable(): boolean {
  const nav = browserNavigator();
  return nav !== null && typeof nav.onLine === 'boolean';
}

export const network: Network = {
  isSupported: onLineCapable,

  isOnline: () => {
    // Optimistic when unknown. A false "offline" would show a parent an
    // offline banner in a working gym, which teaches them to ignore it.
    if (!onLineCapable()) return true;
    return browserNavigator()?.onLine ?? true;
  },

  onChange: (listener) => {
    const win = browserWindow();
    if (win === null) return () => {};

    const online = () => {
      listener(true);
    };
    const offline = () => {
      listener(false);
    };
    win.addEventListener('online', online);
    win.addEventListener('offline', offline);

    return () => {
      win.removeEventListener('online', online);
      win.removeEventListener('offline', offline);
    };
  },
};
